// Best effort has been made to keep all constants pertaining to game-eft in here.
import type { WikiItem } from "@/game-eft/models/model-wiki.mjs";
import heroImg from "@/inline-assets/game-unknown-hero.webp";
import { svg as unknownIcon } from "@/inline-assets/game-unknown-icon.svg";
import keyartImg from "@/inline-assets/game-unknown-keyart.webp";
import nameImg from "@/inline-assets/game-unknown-name.webp";
import rangeBucket from "@/util/range-bucket.mjs";

// Note to implementers: do not inline assets for other games, use the CDN.
// This one is kind of an exception.
export const gameLibraryEntry = {
  hero: heroImg,
  banner: null, // this one is optional
  keyart: keyartImg,
  wordmark: nameImg,
};

export const gameUnknownIcon = `data:image/svg+xml;base64,${btoa(unknownIcon)}`;

// Used for BE
export enum FactionBE {
  BEAR = "BEAR",
  SCAV = "SCAV",
  USEC = "USEC",
}

// Used for RE
export enum FactionRE {
  usec = "usec",
  savage = "savage", // SCAV equivalent for BE
  bear = "bear",
}

export const FactionMap = {
  [FactionBE.BEAR]: FactionRE.bear,
  [FactionBE.SCAV]: FactionRE.savage,
  [FactionBE.USEC]: FactionRE.usec,
  [FactionRE.bear]: FactionBE.BEAR,
  [FactionRE.savage]: FactionBE.SCAV,
  [FactionRE.usec]: FactionBE.USEC,
};

// Used for BE
export enum GameMode {
  ARENA = "ARENA",
  DEFAULT = "DEFAULT",
}

export const gameModes = [
  {
    key: GameMode.DEFAULT,
    text: ["eft:match.stats.extraction", "Extraction"],
    disabled: false,
  },
  {
    key: GameMode.ARENA,
    text: ["lol:arena", "Arena"],
    disabled: true,
    tooltip: ["common:comingSoonNormal", "Coming Soon"] as Translation,
  },
];

// Used for BE
export enum BodyPartBE {
  BODY = "BODY",
  CHEST = "CHEST",
  HEAD = "HEAD",
  LEFT_ARM = "LEFT_ARM",
  LEFT_LEG = "LEFT_LEG",
  RIGHT_ARM = "RIGHT_ARM",
  RIGHT_LEG = "RIGHT_LEG",
  STOMACH = "STOMACH",
}

// Used for RE
export enum BodyPartRE {
  Head = 0,
  Chest = 1,
  Stomach = 2,
  LeftArm = 3,
  RightArm = 4,
  LeftLeg = 5,
  RightLeg = 6,
  Common = 7,
}

export const BodyPartMap = {
  [BodyPartBE.CHEST]: BodyPartRE.Chest,
  [BodyPartBE.HEAD]: BodyPartRE.Head,
  [BodyPartBE.LEFT_ARM]: BodyPartRE.LeftArm,
  [BodyPartBE.LEFT_LEG]: BodyPartRE.LeftLeg,
  [BodyPartBE.RIGHT_ARM]: BodyPartRE.RightArm,
  [BodyPartBE.RIGHT_LEG]: BodyPartRE.RightLeg,
  [BodyPartBE.STOMACH]: BodyPartRE.Stomach,
  [BodyPartBE.BODY]: BodyPartRE.Common,
  [BodyPartRE.Chest]: BodyPartBE.CHEST,
  [BodyPartRE.Head]: BodyPartBE.HEAD,
  [BodyPartRE.LeftArm]: BodyPartBE.LEFT_ARM,
  [BodyPartRE.LeftLeg]: BodyPartBE.LEFT_LEG,
  [BodyPartRE.RightArm]: BodyPartBE.RIGHT_ARM,
  [BodyPartRE.RightLeg]: BodyPartBE.RIGHT_LEG,
  [BodyPartRE.Stomach]: BodyPartBE.STOMACH,
  [BodyPartRE.Common]: BodyPartBE.BODY,
};

export const MapRE = {
  bigmap: "Customs",
  Lighthouse: "Lighthouse",
  Sandbox: "Ground Zero",
  Sandbox_high: "Ground Zero",
  factory4_day: "Factory",
  Shoreline: "Shoreline",
  Interchange: "Interchange",
  TarkovStreets: "Streets",
  laboratory: "The Lab",
  Woods: "Woods",
  factory4_night: "Factory",
  RezervBase: "Reserve",
  hideout: "Hideout",
} as const;

// Used for BE
export enum ExitReason {
  KILLED = "KILLED",
  LEFT = "LEFT",
  MISSING_IN_ACTION = "MISSING_IN_ACTION",
  RUNNER = "RUNNER",
  SURVIVED = "SURVIVED",
}
// Used for RE
export enum EndReason {
  Survived = "Survived",
  Killed = "Killed",
  Left = "Left",
  Runner = "Runner",
  MissingInAction = "MissingInAction",
}

export enum ItemType {
  AMMO = "AMMO",
  AMMO_BOX = "AMMO_BOX",
  ARMORED_EQUIPMENT = "ARMORED_EQUIPMENT",
  ARM_BAND = "ARM_BAND",
  ASSAULT_CARBINE = "ASSAULT_CARBINE",
  ASSAULT_RIFLE = "ASSAULT_RIFLE",
  BACKPACK = "BACKPACK",
  BARTER = "BARTER",
  DRINK = "DRINK",
  DRUGS = "DRUGS",
  FOOD = "FOOD",
  GRENADE_LAUNCHER = "GRENADE_LAUNCHER",
  HEADPHONES = "HEADPHONES",
  HIDEOUT_CONTAINER = "HIDEOUT_CONTAINER",
  INFO = "INFO",
  ITEM = "ITEM",
  KEY = "KEY",
  KNIFE = "KNIFE",
  MACHINE_GUN = "MACHINE_GUN",
  MAP = "MAP",
  MARKSMAN_RIFLE = "MARKSMAN_RIFLE",
  MEDICAL = "MEDICAL",
  MEDS = "MEDS",
  MED_KIT = "MED_KIT",
  MOD = "MOD",
  MONEY = "MONEY",
  PISTOL = "PISTOL",
  POCKETS = "POCKETS",
  REOVLER = "REOVLER",
  SEARCHABLE_ITEM = "SEARCHABLE_ITEM",
  SHOTGUN = "SHOTGUN",
  SIMPLE_CONTAINER = "SIMPLE_CONTAINER",
  SMG = "SMG",
  SNIPER_RIFLE = "SNIPER_RIFLE",
  SPECIAL = "SPECIAL",
  STIMULATOR = "STIMULATOR",
  THROWABLE_WEAPON = "THROWABLE_WEAPON",
  VEST = "VEST",
  WEAPON = "WEAPON",
}
export const DEFAULT_MATCHES_LIMIT: number = 20;

export enum SearchParamsEnum {
  MatchTileGameId = "matchTileGameId",
  MatchTileTab = "matchTileTab",
  GameMode = "gameMode",
  Season = "season",
  Faction = "faction",
  WeaponName = "weaponName",
  MapName = "mapName",
  Leaderboard = "leaderboard",
}

export enum MatchTileTabsEnum {
  Scoreboard = "scoreboard",
  Statistics = "statistics",
  Hits = "weaponKills",
  Weapons = "weaponHits",
}

export enum LeaderboardsTabsEnum {
  Players = "players",
  Weapons = "weapons",
  Maps = "maps",
  Arena = "arena",
}

export enum LeaderboardMetricsEnum {
  SUM_RAIDS = "SUM_RAIDS",
  SURVIVAL_RATE = "SURVIVAL_RATE",
  SUM_KILLS = "SUM_KILLS",
  AVG_KILLS = "AVG_KILLS",
  AVG_DMG = "AVG_DMG",
  SUM_LOOT = "SUM_LOOT",
  AVG_LOOT = "AVG_LOOT",
}

interface LeaderboardSortData {
  value: LeaderboardMetricsEnum;
  label: Translation;
  disabled?: boolean;
}

export const LeaderboardSortParamMap: Record<string, LeaderboardSortData> = {
  raids: {
    value: LeaderboardMetricsEnum.SUM_RAIDS,
    label: ["eft:leaderboard.sort.highestTotalRaids", "Most Raids"],
  },
  kills: {
    value: LeaderboardMetricsEnum.SUM_KILLS,
    label: ["eft:leaderboard.sort.highestTotalKills", "Most Kills"],
  },
  survival: {
    value: LeaderboardMetricsEnum.SURVIVAL_RATE,
    label: ["eft:leaderboard.sort.highestSurvival", "Highest S/R"],
    disabled: true,
  },
  damage: {
    value: LeaderboardMetricsEnum.AVG_DMG,
    label: ["eft:leaderboard.sort.highestAvgDamage", "Highest Avg. Damage"],
    disabled: true,
  },
  loot: {
    value: LeaderboardMetricsEnum.SUM_LOOT,
    label: ["eft:leaderboard.sort.highestLootValue", "Highest Loot Value"],
    disabled: true,
  },
  avgKills: {
    value: LeaderboardMetricsEnum.AVG_KILLS,
    label: ["eft:leaderboard.sort.highestAvgKills", "Highest Avg. Kills"],
    disabled: true,
  },
  avgLoot: {
    value: LeaderboardMetricsEnum.AVG_LOOT,
    label: ["eft:leaderboard.sort.highestAvgLoot", "Highest Avg. Loot Value"],
    disabled: true,
  },
};

export const DEFAULT_SORT_BY =
  "raids" as const satisfies keyof typeof LeaderboardSortParamMap;

export enum TarkovCurrency {
  RUB = "RUB",
  USD = "USD",
  EUR = "EUR",
}

export const CurrencyIDMap = {
  [TarkovCurrency.RUB]: "5449016a4bdc2d6f028b456f",
  [TarkovCurrency.USD]: "5696686a4bdc2da3298b456a",
  [TarkovCurrency.EUR]: "569668774bdc2da2298b4568",
};

const bgAlpha = 0.75;
export const TarkovColorMap: Record<
  NonNullable<WikiItem["bgColor"]>,
  string
> = {
  black: `rgba(0, 0, 0, ${bgAlpha})`,
  blue: `rgba(28, 65, 86, ${bgAlpha})`,
  default: `rgba(127, 127, 127, ${bgAlpha})`,
  green: `rgba(21, 45, 0, ${bgAlpha})`,
  grey: `rgba(29, 29, 29, ${bgAlpha})`,
  orange: `rgba(60, 25, 0, ${bgAlpha})`,
  red: `rgba(109, 36, 24, ${bgAlpha})`,
  violet: `rgba(76, 42, 85, ${bgAlpha})`,
  yellow: `rgba(104, 102, 40, ${bgAlpha})`,
};

export const tradeValueColor = rangeBucket([
  [-Infinity, "var(--red)"],
  [0, "var(--shade0)"],
  [1, "var(--turq)"],
]);

export const PROFILE_MATCHTILE_TABS = [
  {
    name: ["tft:matchtabs.scoreboard", "Scoreboard"] as Translation,
    key: MatchTileTabsEnum.Scoreboard,
  },
  {
    name: ["common:navigation.statistics", "Statistics"] as Translation,
    shortName: ["common:stats", "Stats"] as Translation,
    key: MatchTileTabsEnum.Statistics,
  },
  {
    name: ["eft:navigation.weaponKills", "Weapon Kills"] as Translation,
    shortName: ["common:stats.kills", "Kills"] as Translation,
    key: MatchTileTabsEnum.Weapons,
  },
  {
    name: ["val:stats.hits", "Hits"] as Translation,
    key: MatchTileTabsEnum.Hits,
  },
];

export const AFFLICTIONS = new Set([
  "blood_loss",
  "contusions",
  "exhaustions",
  "dehydrations",
  "fractures",
]);

export const IS_ALIVE: Set<unknown> = new Set([
  ExitReason.SURVIVED,
  ExitReason.RUNNER,
]);
export const MAX_MATCHES = 20;
export const BOT_ACCOUNT_ID = "0";
export const MATCHLIST_OFFSET = 10;
export const PLAYER_LEADERBOARD_PAGE_LIMIT = 5;
